import React, { useEffect, useRef } from 'react';
import { ConfigProvider } from '@app/ui';
import './SocialPosts.scss';

export type StickySocialPostsTableLayoutProps = {
    children: React.ReactNode;
    /** Optional test id on the outer scroll wrapper */
    'data-test'?: string;
    /** Extra class names on the outer `.always-scroll-wrapper` */
    wrapperClassName?: string;
    /**
     * When true, sets the inner `.ant-table-content` horizontal scroll to the
     * right edge after layout (e.g. after switching to organic/paid metric columns).
     */
    scrollContentToEndWhen?: boolean;
    /**
     * Additional dependencies for the scroll-to-end effect. Use values that change
     * when column structure changes (e.g. `[columns]` from useMemo).
     */
    scrollContentToEndDependencies?: React.DependencyList;
    /** Match Social Media Exposure table header / selection colors */
    withExposureTableTheme?: boolean;
};

const EMPTY_DEPS: React.DependencyList = [];

/**
 * Shared shell for Social Media Exposure and Social Post Metrics tables:
 * sticky header offset CSS (see `SocialPosts.scss`), gap mask under sticky filters,
 * and optional horizontal scroll-to-end for wide metric layouts.
 */
const StickySocialPostsTableLayout = ({
    children,
    'data-test': dataTest,
    wrapperClassName,
    scrollContentToEndWhen = false,
    scrollContentToEndDependencies,
    withExposureTableTheme = true,
}: StickySocialPostsTableLayoutProps) => {
    const wrapperRef = useRef<HTMLDivElement>(null);
    const extraDeps = scrollContentToEndDependencies ?? EMPTY_DEPS;

    useEffect(() => {
        if (!scrollContentToEndWhen || !wrapperRef.current) return;

        const tableContent =
            wrapperRef.current.querySelector<HTMLElement>(
                '.ant-table-content',
            );

        if (tableContent) {
            tableContent.scrollLeft = tableContent.scrollWidth;
        }
        // scrollContentToEndDependencies is an intentional dependency bundle from callers
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [scrollContentToEndWhen, ...extraDeps]);

    const shell = (
        <div
            className={
                wrapperClassName
                    ? `always-scroll-wrapper ${wrapperClassName}`
                    : 'always-scroll-wrapper'
            }
            ref={wrapperRef}
            data-test={dataTest}
        >
            <div
                className="social-posts-sticky-gap-mask"
                aria-hidden="true"
            />
            {children}
        </div>
    );

    if (!withExposureTableTheme) {
        return shell;
    }

    return (
        <ConfigProvider
            theme={{
                token: {
                    fontSize: 14,
                    fontFamily: 'pnregular',
                },
                components: {
                    Table: {
                        headerBg: '#A3BDC7',
                        borderColor: '#0000000F',
                        rowSelectedBg: '#A3BDC7',
                        rowSelectedHoverBg: '#A3BDC7',
                    },
                },
            }}
        >
            {shell}
        </ConfigProvider>
    );
};

export default StickySocialPostsTableLayout;
